// client/src/services/periodSummaryService.ts (converted from .js, ST-00229)
//
// Period Summary API client (ST-00174).
//
// Thin wrapper over the shared axios instance in `services/api` so the page
// inherits the cookie-session auth interceptors, CSRF header, single-flight 401
// refresh and demo-mode masking. NOTE: `api` already carries `/api` in its
// baseURL, so paths here are relative to that.
//
// Every helper resolves to the response BODY (not the axios response) — the
// page never needs the envelope. On failure it rejects with the axios error,
// whose `message` carries the server's reason when the API gave one (ST-00229,
// audit ANL-21): /period-summary answers `{ error: '<reason>' }`, and the page's
// banners (summary, breakdown, Tickets tab, Timeline) fall back to
// `err.message`, which axios had set to "Request failed with status code 404".
//
// Keys (Ref No / Order No) can contain slashes, so they are always passed as
// query params, never as path segments. axios encodes `params` for us.

import api from './api';
import type {
  PeriodBreakdownBody,
  PeriodBreakdownParams,
  PeriodGroupingsBody,
  PeriodQueryParams,
  PeriodSummaryBody,
  PeriodTicketsBody,
  PeriodTicketsParams,
  PeriodTimelineBody,
  PeriodTimelineParams,
} from '../types/clientPages/periodSummary';

const BASE = '/period-summary';

/**
 * Drop empty/undefined values so we never send `ticketType=` (which the API
 * would have to treat as "no filter" anyway) or an empty search.
 *
 * `dispatchLocation` / `receiptLocation` are the exception: the API reports
 * locations that are blank in the source as `''`, and filtering to one of those
 * is a legitimate (if rare) drill-in — so an empty STRING is kept for those two
 * and only null/undefined drops them. Sending nothing means "any location".
 */
const ROUTE_PARAMS = ['dispatchLocation', 'receiptLocation'];

const clean = (params: object = {}): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(params).filter(([k, v]) => {
      if (v === undefined || v === null) return false;
      if (v === '') return ROUTE_PARAMS.includes(k);
      return true;
    })
  );

/** The parts of an axios error read here. */
interface HttpErrorLike {
  message?: string;
  response?: { data?: unknown };
}

/** The reason in an error body: `message` first (as the page reads it), then `error`. */
const bodyReason = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') return null;
  const { message, error } = data as { message?: unknown; error?: unknown };
  for (const value of [message, error]) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
};

/**
 * Re-throw a failed request with the server's reason as its message (ANL-21).
 * The error object itself (status, response, isAxiosError) is kept as it was.
 */
export const withServerReason = (err: unknown): never => {
  if (err && typeof err === 'object') {
    const reason = bodyReason((err as HttpErrorLike).response?.data);
    if (reason) {
      try {
        (err as HttpErrorLike).message = reason;
      } catch {
        // A frozen error keeps axios' own text.
      }
    }
  }
  throw err;
};

const getBody = <T>(path: string, params: object, timeout: number): Promise<T> =>
  api
    .get(path, { params: clean(params), timeout })
    .then((res: { data: T }) => res.data, withServerReason);

/** One summarised row per key value for the period. */
export const getPeriodSummary = (params: PeriodQueryParams): Promise<PeriodSummaryBody> =>
  getBody<PeriodSummaryBody>(BASE, params, 120000);

/** Breakdown of one group by product / route / day / ticket_type / haulier. */
export const getPeriodSummaryBreakdown = (
  params: PeriodBreakdownParams
): Promise<PeriodBreakdownBody> => getBody<PeriodBreakdownBody>(`${BASE}/breakdown`, params, 60000);

/** Paged underlying tickets for one group. */
export const getPeriodSummaryTickets = (params: PeriodTicketsParams): Promise<PeriodTicketsBody> =>
  getBody<PeriodTicketsBody>(`${BASE}/tickets`, params, 60000);

/**
 * Per-group daily activity for the timeline view. `days` at the top level is the
 * full calendar of the period; each group's `days` array is SPARSE (active days
 * only), so the caller builds the grid by date.
 */
export const getPeriodSummaryTimeline = (
  params: PeriodTimelineParams
): Promise<PeriodTimelineBody> => getBody<PeriodTimelineBody>(`${BASE}/timeline`, params, 120000);

/**
 * The tenant's Route Groupings (Settings → Route Summary) with their ticket /
 * key counts in the period, A–Z, for the toolbar's route filter. Every
 * grouping is listed, zero counts included.
 */
export const getPeriodSummaryGroupings = (
  params: PeriodQueryParams
): Promise<PeriodGroupingsBody> => getBody<PeriodGroupingsBody>(`${BASE}/groupings`, params, 60000);

const periodSummaryService = {
  getSummary: getPeriodSummary,
  getBreakdown: getPeriodSummaryBreakdown,
  getTickets: getPeriodSummaryTickets,
  getTimeline: getPeriodSummaryTimeline,
  getGroupings: getPeriodSummaryGroupings,
};

export default periodSummaryService;
